import React, { useState, useEffect } from 'react';
import Modal from '../../../../components/Modal';
import AddSwapTransactionForm, { SwapTransactionFormData } from './AddSwapTransactionForm';
import { 
  getSwapListAPI, 
  saveSwapAPI, 
  SwapResponse,
  SwapSearch,
  SwapRequest,
  getPartyListAPI,
  getTokenListAPI,
  Party,
  Token,
  deleteSwapAPI
} from '../../../../services/adminApi';
import { ToastContainer } from '../../../../components/Toast';
import { useToast } from '../../../../hooks/useToast';
import { useModulePermission } from '../../../../hooks/useModulePermission';
import styles from './Transactions.module.css';

const Swap: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [transactions, setTransactions] = useState<SwapResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTransaction, setEditingTransaction] = useState<SwapResponse | null>(null);
  const [parties, setParties] = useState<any[]>([]);
  const [tokens, setTokens] = useState<any[]>([]);
  const today = new Date().toISOString().split('T')[0];
  const [filterData, setFilterData] = useState({
    fromDate: today,
    toDate: today,
    party: '',
    swapType: '',
    payIn: '',
  });
  const { toasts, removeToast, showSuccess, showError } = useToast();
  const permissions = useModulePermission('Swap');

  // Load transactions on component mount and when filters change
  useEffect(() => {
    loadTransactions();
    loadFilterData();
  }, [filterData, searchQuery]);

  const loadFilterData = async () => {
    try {
      const [partiesResponse, tokensResponse] = await Promise.all([
        getPartyListAPI({ pageNo: 1, pageSize: 100 }),
        getTokenListAPI({ pageNo: 1, pageSize: 100 }),
      ]);
      setParties((partiesResponse.data || []).filter(p => p.isActive));
      setTokens((tokensResponse.data || []).filter(t => t.isActive));
    } catch (error) {
      console.error('Failed to load filter data:', error);
    }
  };

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const searchRequest: SwapSearch = {
        partyId:0,
        swapTypeId:0,
        paymentInId:0,
        searchText:'',
        pageNo: 1,
        pageSize: 100,
      };
      
      // Add filter fields only if they have values
      if (filterData.fromDate) searchRequest.fromDate = filterData.fromDate;
      if (filterData.toDate) searchRequest.toDate = filterData.toDate;
      if (filterData.party) searchRequest.partyId = parseInt(filterData.party);
      if (filterData.swapType) {
        const swapTypeMap: { [key: string]: number } = {
          'INR to Token': 1,
          'Token to INR': 2,
          'Dollar to INR': 3,
          'INR to Dollar': 4,
          'Dollar to Token': 5,
          'Token to Dollar': 6
        };
        searchRequest.swapTypeId = swapTypeMap[filterData.swapType];
      }
      if (filterData.payIn) {
        const payInMap: { [key: string]: number } = {
          'INR': 1,
          'USD': 2,
          'Token': 3
        };
        searchRequest.paymentInId = payInMap[filterData.payIn];
      }
      if (searchQuery) searchRequest.searchText = searchQuery;
      
      const response = await getSwapListAPI(searchRequest);
      if (response.isSuccess && response.data) {
        setTransactions(response.data);
      } else {
        showError(response.message || 'Failed to load swap transactions');
      }
    } catch (error) {
      console.error('Failed to load swap transactions:', error);
      showError('Failed to load swap transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTransaction = () => {
    setEditingTransaction(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTransaction(null);
  };

  const handleSubmitTransaction = async (transactionData: SwapTransactionFormData) => {
    try {
      // Determine fromAccountId and toAccountId based on swap type
      let fromAccountId: number | undefined;
      let toAccountId: number | undefined;

      // Determine FROM Account ID
      if (transactionData.swapType === 'INR to Token' || transactionData.swapType === 'INR to Dollar') {
        fromAccountId = transactionData.fromAccountId ? parseInt(transactionData.fromAccountId) : undefined;
      } else if (transactionData.swapType === 'Dollar to INR' || transactionData.swapType === 'Dollar to Token') {
        fromAccountId = transactionData.fromDollarAccountId ? parseInt(transactionData.fromDollarAccountId) : undefined;
      } else if (transactionData.swapType === 'Token to INR' || transactionData.swapType === 'Token to Dollar') {
        // For token swaps, fromAccountId is the token ID being sold
        fromAccountId = transactionData.toTokenId ? parseInt(transactionData.toTokenId) : undefined;
      }

      // Determine TO Account ID
      if (transactionData.swapType === 'INR to Token') {
        toAccountId = transactionData.toTokenId ? parseInt(transactionData.toTokenId) : undefined;
      } else if (transactionData.swapType === 'Token to INR') {
        toAccountId = transactionData.toAccountId ? parseInt(transactionData.toAccountId) : undefined;
      } else if (transactionData.swapType === 'INR to Dollar') {
        toAccountId = transactionData.toDollarAccountId ? parseInt(transactionData.toDollarAccountId) : undefined;
      } else if (transactionData.swapType === 'Dollar to INR') {
        toAccountId = transactionData.toAccountId ? parseInt(transactionData.toAccountId) : undefined;
      } else if (transactionData.swapType === 'Dollar to Token') {
        toAccountId = transactionData.toTokenId ? parseInt(transactionData.toTokenId) : undefined;
      } else if (transactionData.swapType === 'Token to Dollar') {
        toAccountId = transactionData.toDollarAccountId ? parseInt(transactionData.toDollarAccountId) : undefined;
      }

      const swapRequest: SwapRequest = {
        id: editingTransaction?.id,
        swapTypeId: transactionData.swapType === 'INR to Token' ? 1 : transactionData.swapType === 'Token to INR' ? 2 : transactionData.swapType === 'Dollar to INR' ? 3 : transactionData.swapType === 'INR to Dollar' ? 4 : transactionData.swapType === 'Dollar to Token' ? 5 : 6,
        swapDate: transactionData.date,
        paymentInId: transactionData.payIn === 'INR' ? 1 : transactionData.payIn === 'USD' ? 2 : 3,
        partyId: transactionData.partyId ? parseInt(transactionData.partyId) : undefined,
        fromAccountId: fromAccountId,
        toAccountId: toAccountId,
        quantity: parseFloat(transactionData.quantity.toString()) || 0,
        tokenToDollarRate: parseFloat(transactionData.tokenRate.toString()) || 0,
        dollarToINRRate: parseFloat(transactionData.dollarRate.toString()) || 0,
        slippagePerct: parseFloat(transactionData.slippagePercent.toString()) || 0,
        baseAmount: parseFloat(transactionData.baseAmount.toString()) || 0,
        slippageAmount: parseFloat(transactionData.slippageAmount.toString()) || 0,
        finalAmount: parseFloat(transactionData.finalAmount.toString()) || 0,
        remarks: transactionData.remarks,
      };

      const response = await saveSwapAPI(swapRequest);
      
      if (response.isSuccess) {
        showSuccess(editingTransaction ? 'Swap transaction updated successfully!' : 'Swap transaction created successfully!');
        await loadTransactions();
        setIsModalOpen(false);
        setEditingTransaction(null);
      } else {
        showError(response.message || 'Failed to save transaction');
      }
    } catch (error) {
      console.error('Failed to save swap transaction:', error);
      showError('Failed to save transaction. Please try again.');
    }
  };

  const handleEdit = (transaction: SwapResponse) => {
    setEditingTransaction(transaction);
    setIsModalOpen(true);
  };

  const handleDelete = async (transaction: SwapResponse) => {
    if (window.confirm(`Are you sure you want to delete this transaction?`)) {
      try {
        if (!transaction.id) {
          showError('Transaction ID is missing');
          return;
        }
        const response = await deleteSwapAPI(transaction.id);
        if (response.isSuccess) {
          showSuccess('Swap transaction deleted successfully!');
          await loadTransactions();
        } else {
          showError(response.message || 'Failed to delete transaction');
        }
      } catch (error) {
        console.error('Failed to delete transaction:', error);
        showError('Failed to delete transaction. Please try again.');
      }
    }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilterData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClearFilters = () => {
    const today = new Date().toISOString().split('T')[0];
    setFilterData({
      fromDate: today,
      toDate: today,
      party: '',
      swapType: '',
      payIn: '',
    });
  };

  const handleExport = () => {
    // TODO: Implement export functionality
    showSuccess('Export functionality coming soon!');
  };

  // Use transactions directly since backend now handles filtering
  const filteredTransactions = transactions;

  return (
    <div className={styles.container}>
      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>Swap Transactions</h1>
          <p className={styles.subtitle}>Exchange between tokens, INR, dollars</p>
        </div>
        <div className={styles.headerActions}>
          <button 
            className={styles.filterButton} 
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            title="Filter"
          >
            🔽 Filters
          </button>
          {permissions.canExport && (
            <button 
              className={styles.exportButton} 
              onClick={handleExport}
              title="Export"
            >
              📥 Export
            </button>
          )}
          {permissions.canAdd && (
            <button className={styles.addButton} onClick={handleAddTransaction}>
              + Add Swap Transaction
            </button>
          )}
        </div>
      </div>

      {/* Filter Panel */}
      {isFilterOpen && (
        <div className={styles.filterPanel}>
          <div className={styles.filterRow}>
            <div className={styles.filterGroup}>
              <label>FROM DATE</label>
              <input
                type="date"
                name="fromDate"
                value={filterData.fromDate}
                onChange={handleFilterChange}
                className={styles.filterInput}
              />
            </div>
            <div className={styles.filterGroup}>
              <label>TO DATE</label>
              <input
                type="date"
                name="toDate"
                value={filterData.toDate}
                onChange={handleFilterChange}
                className={styles.filterInput}
              />
            </div>
            <div className={styles.filterGroup}>
              <label>PARTY</label>
              <select
                name="party"
                value={filterData.party}
                onChange={handleFilterChange}
                className={styles.filterSelect}
              >
                <option value="">All Parties</option>
                {parties.map((party) => (
                  <option key={party.id} value={party.id}>
                    {party.partyName}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.filterGroup}>
              <label>SWAP TYPE</label>
              <select
                name="swapType"
                value={filterData.swapType}
                onChange={handleFilterChange}
                className={styles.filterSelect}
              >
                <option value="">All Types</option>
                <option value="INR to Token">INR to Token</option>
                <option value="Token to INR">Token to INR</option>
                <option value="Dollar to INR">Dollar to INR</option>
                <option value="INR to Dollar">INR to Dollar</option>
                <option value="Dollar to Token">Dollar to Token</option>
                <option value="Token to Dollar">Token to Dollar</option>
              </select>
            </div>
            <div className={styles.filterGroup}>
              <label>PAY IN</label>
              <select
                name="payIn"
                value={filterData.payIn}
                onChange={handleFilterChange}
                className={styles.filterSelect}
              >
                <option value="">All</option>
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="Token">Token</option>
              </select>
            </div>
          </div>
          <div className={styles.filterActions}>
            <button 
              className={styles.clearButton}
              onClick={handleClearFilters}
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.loading}>Loading transactions...</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>DATE</th>
                <th>TYPE</th>
                <th>PARTY</th>
                <th>FROM</th>
                <th>TO</th>
                <th>QTY</th>
                <th>TOKEN/$</th>
                <th>$/INR</th>
                <th>SLIP%</th>
                <th>FINAL AMT</th>
                <th>PAY IN</th>
                {(permissions.canEdit || permissions.canVoid) && <th>ACTIONS</th>}
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan={permissions.canEdit || permissions.canVoid ? 12 : 11} className={styles.emptyState}>No transactions found</td>
                </tr>
              ) : (
                filteredTransactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>{transaction.swapDate ? new Date(transaction.swapDate).toLocaleDateString('en-IN') : '-'}</td>
                    <td>
                      <span className={`${styles.typeBadge} ${styles[transaction.swapType?.toLowerCase().replace(/ /g, '-') || '']}`}>
                        {transaction.swapType || '-'}
                      </span>
                    </td>
                    <td>{transaction.partyName || 'N/A'}</td>
                    <td>{transaction.fromAccountName || '-'}</td>
                    <td>{transaction.toAccountName || '-'}</td>
                    <td>{transaction.quantity || 0}</td>
                    <td>{transaction.tokenToDollarRate || 0}</td>
                    <td>{transaction.dollarToINRRate || 0}</td>
                    <td>{transaction.slippagePerct || 0}%</td>
                    <td>₹{(transaction.finalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td>{transaction.paymentIn || '-'}</td>
                    {(permissions.canEdit || permissions.canVoid) && (
                      <td>
                        <div className={styles.actions}>
                          {permissions.canEdit && (
                            <button
                              className={styles.actionIcon}
                              onClick={() => handleEdit(transaction)}
                              title="Edit"
                            >
                              ✏️
                            </button>
                          )}
                          {permissions.canVoid && (
                            <button
                              className={`${styles.actionIcon} ${styles.deleteIcon}`}
                              onClick={() => handleDelete(transaction)}
                              title="Delete"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Transaction Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingTransaction ? 'Edit Swap Transaction' : 'Add Swap Transaction'}
      >
        <AddSwapTransactionForm
          onSubmit={handleSubmitTransaction}
          onCancel={handleCloseModal}
          initialData={editingTransaction ? {
            date: editingTransaction.swapDate ? editingTransaction.swapDate.split('T')[0] : new Date().toISOString().split('T')[0],
            swapType: editingTransaction.swapType === 'INR to Token' ? 'INR to Token' : editingTransaction.swapType === 'Token to INR' ? 'Token to INR' : editingTransaction.swapType === 'Dollar to INR' ? 'Dollar to INR' : editingTransaction.swapType === 'INR to Dollar' ? 'INR to Dollar' : editingTransaction.swapType === 'Dollar to Token' ? 'Dollar to Token' : 'Token to Dollar',
            payIn: editingTransaction.paymentIn || 'INR',
            partyId: editingTransaction.partyId?.toString() || '',
            partyName: editingTransaction.partyName || '',
            fromAccountId: editingTransaction.fromAccountId?.toString() || '',
            fromAccount: editingTransaction.fromAccountName || '',
            fromDollarAccountId: editingTransaction.fromAccountId?.toString() || '',
            fromDollarAccount: editingTransaction.fromAccountName || '',
            fromTokenId: editingTransaction.fromAccountId?.toString() || '',
            fromToken: editingTransaction.fromAccountName || '',
            toAccountId: editingTransaction.toAccountId?.toString() || '',
            toAccount: editingTransaction.toAccountName || '',
            toTokenId: editingTransaction.toAccountId?.toString() || '',
            toToken: editingTransaction.toAccountName || '',
            toDollarAccountId: editingTransaction.toAccountId?.toString() || '',
            toDollarAccount: editingTransaction.toAccountName || '',
            quantity: editingTransaction.quantity || 0,
            tokenRate: editingTransaction.tokenToDollarRate || 0,
            dollarRate: editingTransaction.dollarToINRRate || 0,
            slippagePercent: editingTransaction.slippagePerct || 0,
            baseAmount: editingTransaction.baseAmount || 0,
            slippageAmount: editingTransaction.slippageAmount || 0,
            finalAmount: editingTransaction.finalAmount || 0,
            remarks: editingTransaction.remarks || '',
          } : undefined}
        />
      </Modal>
    </div>
  );
};

export default Swap;
